import { z } from "zod/v4";

export const SHELL_HOME_PATH = "/shell" as const;
export const SHELL_NEW_PATH = "/shell/new" as const;
export const SHELL_THREAD_PATH = "/shell/[threadCuid]" as const;

export const E_ShellRouteKind = {
  HOME: "home",
  INVALID: "invalid",
  NEW: "new",
  THREAD: "thread",
} as const;

export type T_ShellRoute =
  | { kind: typeof E_ShellRouteKind.HOME; threadCuid: null }
  | { kind: typeof E_ShellRouteKind.NEW; threadCuid: null }
  | { kind: typeof E_ShellRouteKind.THREAD; threadCuid: string }
  | { kind: typeof E_ShellRouteKind.INVALID; threadCuid: null };

export type T_ShellThreadHref = {
  params: { threadCuid: string };
  pathname: typeof SHELL_THREAD_PATH;
};

const z_ShellThreadCuid = z.cuid2();

function normalizePathname(pathname: string): string {
  if (pathname === "/") return pathname;
  return pathname.replace(/\/+$/, "");
}

function getSingleRouteParam(
  value: string | string[] | undefined,
): string | null {
  if (typeof value === "string") return value;
  if (Array.isArray(value) && value.length === 1) return value[0] ?? null;
  return null;
}

export function parseShellThreadCuid(value: unknown): string | null {
  const parsed = z_ShellThreadCuid.safeParse(value);
  return parsed.success ? parsed.data : null;
}

export function resolveShellRoute(
  pathname: string,
  threadCuidParam?: string | string[],
): T_ShellRoute {
  const normalizedPathname = normalizePathname(pathname);
  if (normalizedPathname === SHELL_HOME_PATH) {
    return { kind: E_ShellRouteKind.HOME, threadCuid: null };
  }
  if (normalizedPathname === SHELL_NEW_PATH) {
    return { kind: E_ShellRouteKind.NEW, threadCuid: null };
  }

  const pathSegments = normalizedPathname.split("/").filter(Boolean);
  if (pathSegments.length !== 2 || pathSegments[0] !== "shell") {
    return { kind: E_ShellRouteKind.INVALID, threadCuid: null };
  }

  const threadCuid = parseShellThreadCuid(getSingleRouteParam(threadCuidParam));
  if (threadCuid === null || pathSegments[1] !== threadCuid) {
    return { kind: E_ShellRouteKind.INVALID, threadCuid: null };
  }

  return { kind: E_ShellRouteKind.THREAD, threadCuid };
}

export function buildShellThreadHref(
  threadCuid: string,
): T_ShellThreadHref | null {
  const parsedThreadCuid = parseShellThreadCuid(threadCuid);
  if (parsedThreadCuid === null) return null;

  return {
    params: { threadCuid: parsedThreadCuid },
    pathname: SHELL_THREAD_PATH,
  };
}

/** Keep per-thread identifiers out of analytics and replay screen names. */
export function normalizeShellScreenPath(pathname: string): string {
  const normalizedPathname = normalizePathname(pathname);
  const pathSegments = normalizedPathname.split("/").filter(Boolean);
  if (
    pathSegments.length === 2 &&
    pathSegments[0] === "shell" &&
    pathSegments[1] !== "new"
  ) {
    return SHELL_THREAD_PATH;
  }
  return normalizedPathname;
}

/**
 * Whether the "New chat" control should read as the active destination.
 *
 * Both platforms start a new chat by clearing the thread and landing on the
 * home route; NEW only appears once a draft exists but has not been saved.
 * Gating on NEW alone left the control permanently unhighlighted on web.
 */
export function isShellNewChatActive(route: T_ShellRoute): boolean {
  return (
    route.kind === E_ShellRouteKind.HOME || route.kind === E_ShellRouteKind.NEW
  );
}
